/**
 * PipelineGlance - Three-column pipeline summary section
 *
 * Shows "At Risk" / "Moving" / "About to close" columns based on stage probabilities.
 * Each column displays total value and top deals in that category.
 * Shows each pipeline separately with subtitles if there's more than one.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useDashboardSectionPref } from '@/hooks/useDashboardSectionPref';
import { usePermissions } from '@/hooks/usePermissions';
import { markOnce } from '@/lib/boot/bootMetrics';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { isTransientError } from '@/lib/errors';
import type { PipelineView } from '@/lib/localStorage/dashboardSectionPrefs';
import type { DashboardMetricsData } from '@/pages/dashboard/hooks/useDashboardData';
import { formatCurrency } from '@/pages/dashboard/useDashboardMetrics';

import { PipelineGlanceSkeleton } from './DashboardHomeSkeleton';
import { PipelineStagesView } from './PipelineStagesView';
import {
  SectionVariantDropdown,
  type SectionVariantOption,
} from './SectionVariantDropdown';

const PIPELINE_VARIANT_OPTIONS = [
  { value: 'bucketed', label: 'Summary' },
  { value: 'stagewise', label: 'Stages' },
] as const satisfies readonly SectionVariantOption<PipelineView>[];

interface PipelineGlanceProps {
  /** Pipeline metrics from useDashboardMetrics */
  metrics: DashboardMetricsData | undefined;
  /** Organization ID for navigation links */
  orgId: number;
  /** Whether import is currently processing (can show simplified UI) */
  isProcessing: boolean;
}

/** Grouped stages by pipeline */
interface PipelineGroupedStages {
  pipelineName: string;
  pipelineLabel: string;
  atRisk: DashboardMetricsData['pipelineStages'];
  moving: DashboardMetricsData['pipelineStages'];
  aboutToClose: DashboardMetricsData['pipelineStages'];
  stages: DashboardMetricsData['pipelineStages'];
}

/**
 * Categorize pipeline stages into risk categories based on position.
 * - At Risk: early stages (first ~1/3 of pipeline)
 * - Moving: middle stages (middle ~1/3 of pipeline)
 * - About to close: late stages (last ~1/3 of pipeline)
 *
 * Excludes:
 * - "No Stage" entries (stageEntityId === '')
 * - Closed stages (probability 0 = Closed Lost, probability 1 = Closed Won)
 * - Stages with no deals
 *
 * @param stageProbabilities - Map of stageEntityId → probability from pipeline metadata
 */
function categorizeStages(
  pipelineStages: DashboardMetricsData['pipelineStages'],
  stageProbabilities: Map<string, number | undefined>
) {
  const atRisk: typeof pipelineStages = [];
  const moving: typeof pipelineStages = [];
  const aboutToClose: typeof pipelineStages = [];

  // Filter to only open pipeline stages (exclude "No Stage" and closed stages)
  // Keep stages with zero deals — they affect bucket position computation
  const openStages = pipelineStages.filter(stage => {
    if (stage.stageEntityId === '') {
      return false;
    }
    const probability = stageProbabilities.get(stage.stageEntityId);
    // Closed stages have probability 0 (Closed Lost) or 1 (Closed Won)
    // Stages with undefined probability (missing metadata) are included — they
    // may be valid open stages whose metadata hasn't loaded yet. Excluding them
    // would hide deals and show empty $0 columns.
    if (probability === 0 || probability === 1) {
      return false;
    }
    return true;
  });

  // If no open stages, return empty buckets
  if (openStages.length === 0) {
    return { atRisk, moving, aboutToClose, openStages };
  }

  // Categorize based on position within ALL open stages (including empty ones)
  // so that a late-stage deal stays in "About to close" even if earlier stages are empty.
  // Use (n-1) as denominator so positions range from 0 to 1 inclusive:
  // - 3 stages: positions 0, 0.5, 1.0 → at risk, moving, about to close
  // - 4 stages: positions 0, 0.33, 0.67, 1.0
  const denominator = Math.max(openStages.length - 1, 1);

  for (let i = 0; i < openStages.length; i++) {
    const stage = openStages[i];
    // Skip stages with no deals for display, but their position still counts
    if (stage.dealCount === 0) {
      continue;
    }
    const relativePosition = i / denominator;

    if (relativePosition < 0.33) {
      atRisk.push(stage);
    } else if (relativePosition < 0.67) {
      moving.push(stage);
    } else {
      aboutToClose.push(stage);
    }
  }

  return { atRisk, moving, aboutToClose, openStages };
}

/**
 * Calculate total value for a category of stages.
 */
function calculateCategoryTotal(
  stages: DashboardMetricsData['pipelineStages']
) {
  return stages.reduce((sum, stage) => sum + stage.totalValue, 0);
}

/**
 * Single column component for a risk category.
 * Styled per Figma: bordered card with row separators
 */
function PipelineColumn({
  title,
  stages,
  totalValue,
  currencyCode,
  orgId,
  pipelineName,
}: {
  title: string;
  stages: DashboardMetricsData['pipelineStages'];
  totalValue: number;
  currencyCode: string;
  orgId: number;
  pipelineName?: string;
}) {
  const pipelineParam = pipelineName
    ? `&pipeline=${encodeURIComponent(pipelineName)}`
    : '';
  return (
    <div className='bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] p-4'>
      {/* Column header */}
      <div className='text-[13px] font-semibold text-neutral-600 dark:text-neutral-400'>
        {title}
      </div>
      {/* Total value */}
      <div className='text-[18px] font-semibold text-neutral-900 dark:text-neutral-100 mt-1'>
        {formatCurrency(totalValue, currencyCode)}
      </div>
      {/* Deal rows with dividers */}
      <div className='divide-y divide-neutral-200 dark:divide-neutral-700 mt-3'>
        {stages.slice(0, 3).map(stage => (
          <Link
            key={stage.stageEntityId}
            to={`/${orgId}/crm/deals?stageId=${encodeURIComponent(stage.stageEntityId)}${pipelineParam}`}
            className='flex items-center justify-between py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 -mx-1 px-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)]'
          >
            <span className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 truncate'>
              {stage.stageLabel}
            </span>
            <span className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 ml-2 whitespace-nowrap'>
              {formatCurrency(stage.totalValue, currencyCode)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Compact mobile summary — single card with three columns showing only
 * category label + total value. Tapping links to deals page.
 */
function PipelineSectionMobile({
  categories,
  currencyCode,
  orgId,
  pipelineName,
}: {
  categories: { title: string; total: number }[];
  currencyCode: string;
  orgId: number;
  pipelineName?: string;
}) {
  const pipelineParam = pipelineName
    ? `?pipeline=${encodeURIComponent(pipelineName)}`
    : '';
  return (
    <Link
      to={`/${orgId}/crm/deals${pipelineParam}`}
      className='block bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] px-4 py-3 active:bg-neutral-50 dark:active:bg-neutral-800 transition-colors'
    >
      <div className='grid grid-cols-3 gap-2'>
        {categories.map(cat => (
          <div key={cat.title}>
            <div className='text-[12px] font-semibold text-neutral-500 dark:text-neutral-400'>
              {cat.title}
            </div>
            <div className='text-[16px] font-semibold text-neutral-900 dark:text-neutral-100 mt-0.5'>
              {formatCurrency(cat.total, currencyCode)}
            </div>
          </div>
        ))}
      </div>
    </Link>
  );
}

/** Single pipeline section with three columns */
function PipelineSection({
  pipelineLabel,
  pipelineName,
  showLabel,
  atRisk,
  moving,
  aboutToClose,
  currencyCode,
  orgId,
}: {
  pipelineLabel: string;
  pipelineName?: string;
  showLabel: boolean;
  atRisk: DashboardMetricsData['pipelineStages'];
  moving: DashboardMetricsData['pipelineStages'];
  aboutToClose: DashboardMetricsData['pipelineStages'];
  currencyCode: string;
  orgId: number;
}) {
  const atRiskTotal = calculateCategoryTotal(atRisk);
  const movingTotal = calculateCategoryTotal(moving);
  const aboutToCloseTotal = calculateCategoryTotal(aboutToClose);

  // Memoized to prevent new array reference on every render (totals still recalculate)
  const categories = useMemo(
    () => [
      { title: 'Early stage', total: atRiskTotal },
      { title: 'Moving', total: movingTotal },
      { title: 'About to close', total: aboutToCloseTotal },
    ],
    [atRiskTotal, movingTotal, aboutToCloseTotal]
  );

  return (
    <div className='space-y-3'>
      {showLabel && (
        <Link
          to={`/${orgId}/crm/deals${pipelineName ? `?pipeline=${encodeURIComponent(pipelineName)}` : ''}`}
          className='block text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 hover:text-[var(--color-fg-brand-primary)] transition-colors'
        >
          {pipelineLabel}
        </Link>
      )}
      {/* Mobile: compact single-card summary */}
      <div className='sm:hidden'>
        <PipelineSectionMobile
          categories={categories}
          currencyCode={currencyCode}
          orgId={orgId}
          pipelineName={pipelineName}
        />
      </div>
      {/* Desktop: full three-column cards with stage rows */}
      <div className='hidden sm:grid sm:grid-cols-3 sm:gap-6'>
        <PipelineColumn
          title='Early stage'
          stages={atRisk}
          totalValue={atRiskTotal}
          currencyCode={currencyCode}
          orgId={orgId}
          pipelineName={pipelineName}
        />
        <PipelineColumn
          title='Moving'
          stages={moving}
          totalValue={movingTotal}
          currencyCode={currencyCode}
          orgId={orgId}
          pipelineName={pipelineName}
        />
        <PipelineColumn
          title='About to close'
          stages={aboutToClose}
          totalValue={aboutToCloseTotal}
          currencyCode={currencyCode}
          orgId={orgId}
          pipelineName={pipelineName}
        />
      </div>
    </div>
  );
}

/**
 * PipelineGlance - Main pipeline summary component
 */
export function PipelineGlance({
  metrics,
  orgId,
  isProcessing: _isProcessing,
}: PipelineGlanceProps) {
  // TODO: Remove this override once onboarding state is fixed
  const isProcessing = _isProcessing;
  const { hasPermission } = usePermissions();
  const canCreateDeal = hasPermission('ahoy_deal', 'ahoy_object_created');
  // Fetch pipelines to group stages by pipeline
  const {
    pipelines,
    isLoading: pipelinesLoading,
    error: pipelinesError,
  } = usePipelines(orgId, 'ahoy_deal');
  // useDashboardSectionPref pipeline view — must be before any conditional returns (Rules of Hooks)
  const [pipelineView, setPipelineView] = useDashboardSectionPref(
    'pipeline',
    'bucketed'
  );

  // `metrics` absent means "no metrics for this org", not "still loading".
  const metricsLoading = metrics?.isLoading ?? false;
  useEffect(() => {
    if (!metricsLoading && !pipelinesLoading) {
      markOnce('section:pipeline:content');
    }
  }, [metricsLoading, pipelinesLoading]);

  // Build stage-to-pipeline mapping (includes probability for closed-stage detection)
  const stageToPipeline = useMemo(() => {
    const map = new Map<
      string,
      { name: string; label: string; probability?: number }
    >();
    for (const pipeline of pipelines) {
      for (const stage of pipeline.stages) {
        if (stage.entity_id) {
          map.set(stage.entity_id, {
            name: pipeline.pipeline_name,
            label: pipeline.label,
            probability: stage.properties.probability,
          });
        }
      }
    }
    return map;
  }, [pipelines]);

  // Build probability lookup from pipeline metadata
  const stageProbabilities = useMemo(() => {
    const map = new Map<string, number | undefined>();
    for (const [entityId, info] of stageToPipeline) {
      map.set(entityId, info.probability);
    }
    return map;
  }, [stageToPipeline]);

  // Memoize fallback stages (for when groupedPipelines is empty but we have metrics)
  const fallbackStages = useMemo(
    () => categorizeStages(metrics?.pipelineStages ?? [], stageProbabilities),
    [metrics?.pipelineStages, stageProbabilities]
  );

  // Whether the active view is stagewise (used in groupedPipelines filter)
  const isStagewise = pipelineView === 'stagewise';

  // Group stages by pipeline and categorize each
  const groupedPipelines = useMemo<PipelineGroupedStages[]>(() => {
    if (!metrics?.pipelineStages || pipelines.length === 0) {
      return [];
    }

    // Group stages by pipeline
    const pipelineMap = new Map<
      string,
      {
        label: string;
        stages: DashboardMetricsData['pipelineStages'];
      }
    >();

    for (const stage of metrics.pipelineStages) {
      const pipelineInfo = stageToPipeline.get(stage.stageEntityId);
      const pipelineName = pipelineInfo?.name ?? 'unknown';
      const pipelineLabel = pipelineInfo?.label ?? 'Pipeline';

      let pipelineData = pipelineMap.get(pipelineName);
      if (!pipelineData) {
        pipelineData = { label: pipelineLabel, stages: [] };
        pipelineMap.set(pipelineName, pipelineData);
      }
      pipelineData.stages.push(stage);
    }

    // Categorize each pipeline's stages
    const result: PipelineGroupedStages[] = [];
    for (const [pipelineName, { label, stages }] of pipelineMap) {
      const { atRisk, moving, aboutToClose, openStages } = categorizeStages(
        stages,
        stageProbabilities
      );
      // Include if there are deals in any bucketed category.
      // In stagewise mode, also include pipelines that have open stages
      // (stagewise renders 0-deal stages, so pipelines must not be dropped).
      const hasBucketedDeals =
        atRisk.length > 0 || moving.length > 0 || aboutToClose.length > 0;
      if (hasBucketedDeals || (isStagewise && openStages.length > 0)) {
        result.push({
          pipelineName,
          pipelineLabel: label,
          atRisk,
          moving,
          aboutToClose,
          stages: openStages,
        });
      }
    }

    return result;
  }, [
    metrics?.pipelineStages,
    pipelines,
    stageToPipeline,
    stageProbabilities,
    isStagewise,
  ]);

  // Show simplified UI during processing (check BEFORE empty state to avoid "No deals yet" during import)
  // Display dashes instead of skeleton loaders during onboarding/processing
  if (isProcessing) {
    return (
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100'>
            Pipeline at a glance
          </h2>
          <SectionVariantDropdown
            sectionKey='pipeline'
            options={PIPELINE_VARIANT_OPTIONS}
            value={pipelineView}
            onChange={setPipelineView}
            ariaLabel='Pipeline view'
          />
        </div>
        {pipelineView === 'stagewise' ? (
          /* Stagewise placeholder: horizontal row of 4 dash cards */
          <div className='flex gap-4 overflow-x-auto pb-3'>
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                className='shrink-0 w-[180px] bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] p-4'
              >
                <div className='text-[13px] font-semibold text-neutral-400 dark:text-neutral-500'>
                  —
                </div>
                <div className='text-[18px] font-semibold text-neutral-400 dark:text-neutral-500'>
                  —
                </div>
                <div className='text-[12px] font-medium text-neutral-400 dark:text-neutral-500'>
                  —
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Mobile: compact single card */}
            <div className='sm:hidden'>
              <div className='bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] px-4 py-3'>
                <div className='grid grid-cols-3 gap-2'>
                  {['Early stage', 'Moving', 'About to close'].map(title => (
                    <div key={title}>
                      <div className='text-[12px] font-semibold text-neutral-500 dark:text-neutral-400'>
                        {title}
                      </div>
                      <div className='text-[16px] font-semibold text-neutral-400 dark:text-neutral-500 mt-0.5'>
                        —
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Desktop: three separate cards */}
            <div className='hidden sm:grid sm:grid-cols-3 sm:gap-6'>
              <div className='bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] p-4'>
                <div className='text-[13px] font-semibold text-neutral-600 dark:text-neutral-400'>
                  Early stage
                </div>
                <div className='text-[18px] font-semibold text-neutral-400 dark:text-neutral-500 mt-1'>
                  —
                </div>
              </div>
              <div className='bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] p-4'>
                <div className='text-[13px] font-semibold text-neutral-600 dark:text-neutral-400'>
                  Moving
                </div>
                <div className='text-[18px] font-semibold text-neutral-400 dark:text-neutral-500 mt-1'>
                  —
                </div>
              </div>
              <div className='bg-white dark:bg-neutral-900 border border-secondary rounded-[4px] p-4'>
                <div className='text-[13px] font-semibold text-neutral-600 dark:text-neutral-400'>
                  About to close
                </div>
                <div className='text-[18px] font-semibold text-neutral-400 dark:text-neutral-500 mt-1'>
                  —
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Error state: API failed to load pipelines and no data resolved
  if (
    pipelinesError &&
    !isTransientError(pipelinesError) &&
    !pipelinesLoading &&
    groupedPipelines.length === 0 &&
    (!metrics?.pipelineStages || metrics.pipelineStages.length === 0)
  ) {
    return (
      <div className='space-y-3'>
        <h2 className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100'>
          Pipeline at a glance
        </h2>
        <div className='text-center py-8 text-[13px] text-neutral-500 dark:text-neutral-400'>
          Could not load pipeline configuration. Please refresh the page.
        </div>
      </div>
    );
  }

  // Loading state: metrics (aggregates + stages) or pipelines still in flight.
  // Checked BEFORE the empty state so an org with deals never flashes
  // "No deals yet" while its numbers load — the skeleton is the same one the
  // page skeleton renders, so the transition is a fade, not a reflow.
  if (metricsLoading || pipelinesLoading) {
    return <PipelineGlanceSkeleton />;
  }

  // Empty state: no metrics, no pipeline stages, or no categorizable deals
  // Use groupedPipelines.length instead of activeDealCount because:
  // 1. activeDealCount includes "No Stage" deals which are excluded from display
  // 2. groupedPipelines reflects what actually renders (empty columns avoided)
  if (
    !metrics ||
    !metrics.pipelineStages ||
    metrics.pipelineStages.length === 0 ||
    (groupedPipelines.length === 0 && pipelines.length > 0)
  ) {
    return (
      <div className='space-y-3'>
        <h2 className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100'>
          Pipeline at a glance
        </h2>
        <div className='text-center py-8 text-[13px] text-neutral-500 dark:text-neutral-400'>
          {canCreateDeal ? (
            <>
              No deals yet.{' '}
              <Link
                to={`/${orgId}/crm/deals`}
                className='text-[var(--color-fg-brand-primary)] hover:text-[var(--color-fg-brand-secondary_hover)] underline'
              >
                Create your first deal
              </Link>{' '}
              to see pipeline overview.
            </>
          ) : (
            'No deals yet.'
          )}
        </div>
      </div>
    );
  }

  // Determine whether to show pipeline labels (only if more than one)
  const hasMultiplePipelines = groupedPipelines.length > 1;

  // Normal state: render pipeline sections stacked vertically
  // - Single pipeline: show "Pipeline at a glance" header, no pipeline-specific label
  // - Multiple pipelines: no main header, show individual pipeline names
  return (
    <div className='space-y-3'>
      {/* Single pipeline header */}
      {!hasMultiplePipelines && (
        <div className='flex items-center justify-between'>
          <h2 className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100'>
            Pipeline at a glance
          </h2>
          <SectionVariantDropdown
            sectionKey='pipeline'
            options={PIPELINE_VARIANT_OPTIONS}
            value={pipelineView}
            onChange={setPipelineView}
            ariaLabel='Pipeline view'
          />
        </div>
      )}
      {/* Multi-pipeline shared header */}
      {hasMultiplePipelines && (
        <div className='flex items-center justify-between'>
          <h2 className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100'>
            Pipeline
          </h2>
          <SectionVariantDropdown
            sectionKey='pipeline'
            options={PIPELINE_VARIANT_OPTIONS}
            value={pipelineView}
            onChange={setPipelineView}
            ariaLabel='Pipeline view'
          />
        </div>
      )}
      <div className={hasMultiplePipelines ? 'space-y-6' : ''}>
        {groupedPipelines.map(pipeline =>
          pipelineView === 'stagewise' ? (
            <div key={pipeline.pipelineName} className='space-y-3'>
              {hasMultiplePipelines && (
                <Link
                  to={`/${orgId}/crm/deals${pipeline.pipelineName ? `?pipeline=${encodeURIComponent(pipeline.pipelineName)}` : ''}`}
                  className='block text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 hover:text-[var(--color-fg-brand-primary)] transition-colors'
                >
                  {pipeline.pipelineLabel}
                </Link>
              )}
              <PipelineStagesView
                stages={pipeline.stages}
                currencyCode={metrics.currencyCode}
                orgId={orgId}
                pipelineName={pipeline.pipelineName}
              />
            </div>
          ) : (
            <PipelineSection
              key={pipeline.pipelineName}
              pipelineLabel={pipeline.pipelineLabel}
              pipelineName={pipeline.pipelineName}
              showLabel={hasMultiplePipelines}
              atRisk={pipeline.atRisk}
              moving={pipeline.moving}
              aboutToClose={pipeline.aboutToClose}
              currencyCode={metrics.currencyCode}
              orgId={orgId}
            />
          )
        )}
        {/* Fallback if groupedPipelines is empty but we have stages: probabilities
            are unavailable, so render without probability data. (Loading is
            handled by the PipelineGlanceSkeleton gate above.) */}
        {groupedPipelines.length === 0 &&
          metrics.pipelineStages.length > 0 &&
          pipelineView === 'stagewise' && (
            <PipelineStagesView
              stages={fallbackStages.openStages}
              currencyCode={metrics.currencyCode}
              orgId={orgId}
            />
          )}
        {groupedPipelines.length === 0 &&
          metrics.pipelineStages.length > 0 &&
          !pipelinesLoading &&
          pipelineView !== 'stagewise' && (
            <PipelineSection
              pipelineLabel='Pipeline'
              showLabel={false}
              atRisk={fallbackStages.atRisk}
              moving={fallbackStages.moving}
              aboutToClose={fallbackStages.aboutToClose}
              currencyCode={metrics.currencyCode}
              orgId={orgId}
            />
          )}
      </div>
    </div>
  );
}
